/**
 * BSV Transaction Signing Module
 *
 * This module provides client-side BSV transaction signing capabilities
 * for the self-custody wallet system. It uses the @bsv/sdk for cryptographic
 * operations.
 *
 * The module is designed to be tree-shakeable - only the primitives and
 * transaction classes needed for signing are imported.
 */

import {
    PrivateKey,
    Transaction,
    P2PKH,
    LockingScript,
    UnlockingScript,
    HD,
    Mnemonic,
} from '@bsv/sdk'
import { requestPersistentStorage } from 'lib/storage-persistence'

/**
 * Input specification for an unsigned transaction
 */
export interface TransactionInputSpec {
    /** Previous transaction ID (hex) */
    txid: string
    /** Output index in the previous transaction */
    vout: number
    /** Amount in satoshis */
    satoshis: number
    /** Locking script (scriptPubKey) in hex */
    scriptPubKey: string
    /** Sequence number; 0xffffffff when absent */
    sequence?: number
    /** Set when someone else already unlocked this input; it is never signed here */
    unlockingScript?: string
    /** An input an HTML ordinal supplied: it must arrive unlocked and is never signed here */
    foreign?: boolean
}

/**
 * Output specification for an unsigned transaction
 */
export interface TransactionOutputSpec {
    /** Locking script in hex */
    script: string
    /** Amount in satoshis */
    satoshis: number
}

/**
 * Unsigned transaction template received from the server
 */
export interface UnsignedTransactionTemplate {
    /** Transaction inputs to sign */
    inputs: TransactionInputSpec[]
    /** Transaction outputs */
    outputs: TransactionOutputSpec[]
    /** Transaction version (default: 1) */
    version?: number
    /** Lock time (default: 0) */
    lockTime?: number
}

/**
 * Result of a signing operation
 */
export interface SigningResult {
    /** Whether signing was successful */
    success: boolean
    /** Signed transaction in raw hex format */
    rawTx?: string
    /** Error message if signing failed */
    error?: string
}

/**
 * Signs a transaction using a private key in WIF format.
 *
 * This function takes an unsigned transaction template and signs all inputs
 * using the provided private key. The transaction template is typically
 * received from the server after being prepared by the BSV service.
 *
 * @param template - Unsigned transaction template with inputs and outputs
 * @param privateKeyWIF - Private key in Wallet Import Format (WIF)
 * @returns SigningResult with the signed raw transaction or error
 *
 * @example
 * ```typescript
 * const result = await signTransaction(
 *   {
 *     inputs: [{ txid: '...', vout: 0, satoshis: 10000, scriptPubKey: '...' }],
 *     outputs: [{ script: '...', satoshis: 9500 }]
 *   },
 *   'L1...' // WIF private key
 * )
 * if (result.success) {
 *   console.log('Signed tx:', result.rawTx)
 * }
 * ```
 */
export async function signTransaction(
    template: UnsignedTransactionTemplate,
    privateKeyWIF: string,
): Promise<SigningResult> {
    try {
        const privateKey = PrivateKey.fromWif(privateKeyWIF)
        const p2pkh = new P2PKH()

        // Create transaction
        const tx = new Transaction(template.version ?? 1, [], [], template.lockTime ?? 0)

        // Add inputs with P2PKH unlocking script template
        for (const input of template.inputs) {
            // An input someone else already unlocked (an HTML ordinal's contract input) is
            // carried as is; this key never signs it.
            if (input.foreign && typeof input.unlockingScript !== 'string') {
                throw new Error('Refusing to sign an input the ordinal supplies')
            }
            if (typeof input.unlockingScript === 'string') {
                tx.addInput({
                    sourceTXID: input.txid,
                    sourceOutputIndex: input.vout,
                    unlockingScript: UnlockingScript.fromHex(input.unlockingScript),
                    sequence: input.sequence ?? 0xffffffff,
                })
                continue
            }

            const lockingScript = LockingScript.fromHex(input.scriptPubKey)

            tx.addInput({
                sourceTXID: input.txid,
                sourceOutputIndex: input.vout,
                unlockingScriptTemplate: p2pkh.unlock(
                    privateKey,
                    'all',
                    false,
                    input.satoshis,
                    lockingScript,
                ),
                sequence: input.sequence ?? 0xffffffff,
            })
        }

        // Add outputs
        for (const output of template.outputs) {
            tx.addOutput({
                lockingScript: LockingScript.fromHex(output.script),
                satoshis: output.satoshis,
            })
        }

        // Sign all inputs
        await tx.sign()

        // Get raw hex
        const rawTx = tx.toHex()

        return {
            success: true,
            rawTx,
        }
    } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error)
        return {
            success: false,
            error: `Failed to sign transaction: ${errorMessage}`,
        }
    }
}

/**
 * Validates that a private key WIF is properly formatted.
 *
 * @param wif - Private key in Wallet Import Format
 * @returns true if the WIF is valid, false otherwise
 */
export function isValidWIF(wif: string): boolean {
    try {
        PrivateKey.fromWif(wif)
        return true
    } catch {
        return false
    }
}

/**
 * Gets the public key and address from a private key WIF.
 *
 * @param wif - Private key in Wallet Import Format
 * @returns Object with publicKey (hex) and address, or null if invalid
 */
export function getKeyInfo(wif: string): { publicKey: string; address: string } | null {
    try {
        const privateKey = PrivateKey.fromWif(wif)
        const publicKey = privateKey.toPublicKey()
        const address = publicKey.toAddress()

        return {
            publicKey: publicKey.toDER('hex') as string,
            address: address,
        }
    } catch {
        return null
    }
}

export interface WalletIdentityInfo {
    paymentAddress: string
    ordinalsAddress: string
}

/**
 * Derives the public wallet identity needed for read-only wallet operations.
 *
 * For the current SHUAllet shape, the payment address comes from `payPk` and
 * the ordinals address comes from `ordPk`.
 */
export function getWalletIdentityInfo(walletJson: {
    payPk?: string
    ordPk?: string
}): WalletIdentityInfo | null {
    const paymentKeyWif = walletJson?.payPk
    const ordinalKeyWif = walletJson?.ordPk

    if (!paymentKeyWif || !ordinalKeyWif) {
        return null
    }

    const paymentKeyInfo = getKeyInfo(paymentKeyWif)
    const ordinalKeyInfo = getKeyInfo(ordinalKeyWif)

    if (!paymentKeyInfo?.address || !ordinalKeyInfo?.address) {
        return null
    }

    return {
        paymentAddress: paymentKeyInfo.address,
        ordinalsAddress: ordinalKeyInfo.address,
    }
}

/** Recovery must validate both keys against the account's trusted public identity. */
export function validateWalletTextForRecovery(
    walletText: string,
    expectedIdentity: WalletIdentityInfo,
): WalletIdentityInfo {
    if (typeof walletText !== 'string' || new TextEncoder().encode(walletText).length > 65536) {
        throw new Error('Invalid wallet backup size')
    }
    let walletIdentity: WalletIdentityInfo | null = null
    try {
        walletIdentity = getWalletIdentityInfo(JSON.parse(walletText))
    } catch {}
    if (
        !walletIdentity ||
        !expectedIdentity?.paymentAddress ||
        !expectedIdentity?.ordinalsAddress ||
        walletIdentity.paymentAddress !== expectedIdentity.paymentAddress ||
        walletIdentity.ordinalsAddress !== expectedIdentity.ordinalsAddress
    ) {
        throw new Error(
            'Recovered wallet keys do not match the expected payment and ordinals addresses',
        )
    }
    return walletIdentity
}

// Plan B-R: the wallet JSON carries 12 BIP39 words as `treechatRootMnemonic`, the future
// BRC-100 root. Yours Wallet's identity path makes the same words open the same identity.
export const WALLET_ROOT_IDENTITY_PATH = "m/0'/236'/0'/0/0"

function walletRootKey(wallet: { treechatRootMnemonic?: unknown }): PrivateKey | null {
    const mnemonic = wallet?.treechatRootMnemonic
    if (typeof mnemonic !== 'string' || mnemonic.trim().split(/\s+/).length !== 12) return null
    const seed = Mnemonic.fromString(mnemonic.trim().split(/\s+/).join(' ')).toSeed()
    return HD.fromSeed(seed).derive(WALLET_ROOT_IDENTITY_PATH).privKey
}

/** Hex public key of the wallet root identity, or null when the wallet has no root words. */
export function walletRootIdentityPublicKey(walletText: string): string | null {
    try {
        return walletRootKey(JSON.parse(walletText))?.toPublicKey().toString() ?? null
    } catch {
        return null
    }
}

export interface WalletRecoveryProof {
    payment: { publicKey: string; signature: string }
    ordinals: { publicKey: string; signature: string }
    root?: { publicKey: string; signature: string }
}

// Domain-separated server challenges for passkey and file-backup ownership proofs.
const WALLET_RECOVERY_PROOF_HEADERS = [
    'Treechat wallet passkey recovery ownership proof v1',
    'Treechat wallet file backup ownership proof v1',
]

/** Sign the exact server-issued UTF-8 message; the SDK hashes it once with SHA-256. */
export function signWalletRecoveryProof(walletText: string, message: string): WalletRecoveryProof {
    if (
        typeof message !== 'string' ||
        !WALLET_RECOVERY_PROOF_HEADERS.some(header => message.startsWith(`${header}\n`)) ||
        message.length > 8192
    ) {
        throw new Error('Invalid wallet recovery proof challenge')
    }
    let wallet: { payPk: string; ordPk: string; treechatRootMnemonic?: string }
    try {
        if (typeof walletText !== 'string' || new TextEncoder().encode(walletText).length > 65536) {
            throw new Error('Invalid wallet size')
        }
        wallet = JSON.parse(walletText)
        if (!getWalletIdentityInfo(wallet)) throw new Error('Invalid wallet')
    } catch {
        throw new Error('Wallet recovery proof requires both wallet keys')
    }
    const messageBytes = Array.from(new TextEncoder().encode(message))
    const sign = (key: PrivateKey) => ({
        publicKey: key.toPublicKey().toDER('hex') as string,
        signature: key.sign(messageBytes).toDER('hex') as string,
    })
    const proof: WalletRecoveryProof = {
        payment: sign(PrivateKey.fromWif(wallet.payPk)),
        ordinals: sign(PrivateKey.fromWif(wallet.ordPk)),
    }
    const root = walletRootKey(wallet)
    if (root) proof.root = sign(root)
    return proof
}

/**
 * Generates a random private key and returns it with its public key and address.
 *
 * @returns Object with wif (private key), publicKey (hex), and address
 */
export function generateRandomKey(): { wif: string; publicKey: string; address: string } {
    const privateKey = PrivateKey.fromRandom()
    const publicKey = privateKey.toPublicKey()

    return {
        wif: privateKey.toWif(),
        publicKey: publicKey.toDER('hex') as string,
        address: publicKey.toAddress(),
    }
}

// ============================================================================
// Shamir Secret Sharing (2-of-2 for self-custody)
// ============================================================================

/**
 * Result of splitting a private key into Shamir shares
 */
export interface ShamirShares {
    /** Share to be stored on Treechat server */
    treechatShare: string
    /** Share to be stored on client device (IndexedDB) */
    clientShare: string
    /** Original address for verification */
    address: string
    /** Original public key for verification */
    publicKey: string
}

/**
 * Splits a private key into 2-of-2 Shamir shares for self-custody.
 *
 * The resulting shares can be recombined using `recombineShares()`.
 * Share format is the BSV SDK's backup format (base58 encoded with metadata).
 *
 * @param privateKeyWIF - Private key in WIF format to split
 * @returns ShamirShares with treechatShare, clientShare, and key info
 *
 * @example
 * ```typescript
 * const key = generateRandomKey()
 * const shares = splitKey(key.wif)
 * // Store shares.treechatShare on server
 * // Store shares.clientShare in IndexedDB
 * ```
 */
export function splitKey(privateKeyWIF: string): ShamirShares {
    const privateKey = PrivateKey.fromWif(privateKeyWIF)
    const publicKey = privateKey.toPublicKey()

    // Split into 2-of-2 shares (threshold=2, total=2)
    const backupShares = privateKey.toBackupShares(2, 2)

    return {
        treechatShare: backupShares[0],
        clientShare: backupShares[1],
        address: publicKey.toAddress(),
        publicKey: publicKey.toDER('hex') as string,
    }
}

/**
 * Recombines 2-of-2 Shamir shares back into a private key.
 *
 * @param treechatShare - Share stored on Treechat server
 * @param clientShare - Share stored on client device
 * @returns The reconstructed private key in WIF format, or null if invalid
 *
 * @example
 * ```typescript
 * const wif = recombineShares(shares.treechatShare, shares.clientShare)
 * if (wif) {
 *   const result = await signTransaction(template, wif)
 * }
 * ```
 */
export function recombineShares(treechatShare: string, clientShare: string): string | null {
    try {
        const privateKey = PrivateKey.fromBackupShares([treechatShare, clientShare])
        return privateKey.toWif()
    } catch {
        return null
    }
}

/**
 * Verifies that shares can be recombined to produce a key matching the expected address.
 *
 * @param treechatShare - Share stored on Treechat server
 * @param clientShare - Share stored on client device
 * @param expectedAddress - The address the recombined key should produce
 * @returns true if shares are valid and produce the expected address
 */
export function verifyShares(
    treechatShare: string,
    clientShare: string,
    expectedAddress: string,
): boolean {
    try {
        const privateKey = PrivateKey.fromBackupShares([treechatShare, clientShare])
        const address = privateKey.toPublicKey().toAddress()
        return address === expectedAddress
    } catch {
        return false
    }
}

// ============================================================================
// IndexedDB Storage for Client Shares
// ============================================================================

const DB_NAME = 'treechat-wallet'
const DB_VERSION = 3
const STORE_NAME = 'client-shares'
const DEVICE_ID_STORE_NAME = 'device-ids'
const WALLET_PAYLOAD_STORE_NAME = 'wallet-payloads'
const CLIENT_SIGNING_DEVICE_ID_STORAGE_PREFIX = 'treechat-client-signing-device-id:'
const runtimeClientSigningDeviceIds = new Map<string, string>()
const LOCAL_WALLET_PAYLOAD_CONTEXT = 'treechat-local-wallet-payload:v2'
const LOCAL_WALLET_PAYLOAD_FORMAT_VERSION = 2
const LOCAL_WALLET_NONCE_BYTES = 12
const SELF_CUSTODY_REINITIALIZE_REQUIRED_PREFIX =
    'This browser must re-set up self-custody before it can recover this wallet.'
const textEncoder = new TextEncoder()
const textDecoder = new TextDecoder()

function missingSelfCustodyWalletPayloadMessage(): string {
    return `${SELF_CUSTODY_REINITIALIZE_REQUIRED_PREFIX} This device does not have a local encrypted wallet package yet.`
}

function legacySelfCustodyWalletPayloadMessage(): string {
    return `${SELF_CUSTODY_REINITIALIZE_REQUIRED_PREFIX} This browser is using an older self-custody wallet format.`
}

function unreadableSelfCustodyWalletPayloadMessage(): string {
    return `${SELF_CUSTODY_REINITIALIZE_REQUIRED_PREFIX} The local encrypted wallet package could not be decrypted with this device's current share pair.`
}

export function isSelfCustodyReinitializationRequiredError(error: unknown): boolean {
    const errorMessage = error instanceof Error ? error.message : String(error)
    return errorMessage.startsWith(SELF_CUSTODY_REINITIALIZE_REQUIRED_PREFIX)
}

function clientSigningDeviceIdStorageKey(userId: string): string {
    return `${CLIENT_SIGNING_DEVICE_ID_STORAGE_PREFIX}${userId}`
}

function persistStoredClientSigningDeviceId(userId: string, deviceId: string): void {
    runtimeClientSigningDeviceIds.set(userId, deviceId)
    if (typeof window === 'undefined') {
        return
    }

    try {
        window.localStorage.setItem(clientSigningDeviceIdStorageKey(userId), deviceId)
    } catch {
        // IndexedDB is authoritative. Some Safari/WebView modes expose
        // localStorage but reject access; keep the current page usable.
    }
}

export function clearStoredClientSigningDeviceId(userId: string): void {
    runtimeClientSigningDeviceIds.delete(userId)
    if (typeof window === 'undefined') {
        return
    }

    try {
        window.localStorage.removeItem(clientSigningDeviceIdStorageKey(userId))
    } catch {}
}

export function getStoredClientSigningDeviceId(userId: string): string | null {
    const runtimeDeviceId = runtimeClientSigningDeviceIds.get(userId)
    if (runtimeDeviceId) return runtimeDeviceId
    if (typeof window === 'undefined') {
        return null
    }

    try {
        return window.localStorage.getItem(clientSigningDeviceIdStorageKey(userId))
    } catch {
        return null
    }
}

/**
 * Record stored in IndexedDB for each user's client share
 */
export interface ClientShareRecord {
    /** User ID (primary key) */
    userId: string
    /** Unique device ID for this user+browser combination */
    deviceId: string
    /** The Shamir client share */
    clientShare: string
    /** BSV address for verification */
    address: string
    /** Timestamp when the share was stored */
    createdAt: number
}

interface DeviceIdRecord {
    userId: string
    deviceId: string
    createdAt: number
}

export interface LocalEncryptedWalletPayloadRecord {
    userId: string
    deviceId: string
    formatVersion: number
    encryptedWalletPayload: string
    encryptedWalletNonce: string
    createdAt: number
}

export interface PersistedSelfCustodyWallet {
    clientShareRecord: ClientShareRecord
    walletPayloadRecord: LocalEncryptedWalletPayloadRecord
    shares: ShamirShares
    walletIdentity: WalletIdentityInfo
}

export interface PreparedRecoveredWallet extends PersistedSelfCustodyWallet {
    userId: string
    deviceId: string
    previousDeviceId: string | null
}

// The baseline remains local to this module and must never be sent with a recovery API request.
const preparedRecoveryStates = new WeakMap<PreparedRecoveredWallet, string>()

export type LocalSelfCustodyPayloadStatus = 'missing' | 'compatible' | 'legacy'

function requireWebCrypto(): Crypto {
    if (typeof crypto === 'undefined' || !crypto.subtle) {
        throw new Error('Web Crypto is not available in this browser')
    }

    return crypto
}

function bytesToBase64Url(bytes: Uint8Array): string {
    let binary = ''
    for (const byte of bytes) {
        binary += String.fromCharCode(byte)
    }

    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/u, '')
}

function base64UrlToBytes(encoded: string): Uint8Array {
    const padding = '='.repeat((4 - (encoded.length % 4 || 4)) % 4)
    const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/') + padding
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)

    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index)
    }

    return bytes
}

async function deriveLocalWalletPayloadKey(walletAccessKeyWif: string): Promise<CryptoKey> {
    const webCrypto = requireWebCrypto()
    const digest = await webCrypto.subtle.digest(
        'SHA-256',
        textEncoder.encode(`${LOCAL_WALLET_PAYLOAD_CONTEXT}:${walletAccessKeyWif}`),
    )

    return webCrypto.subtle.importKey(
        'raw',
        digest,
        {
            name: 'AES-GCM',
            length: 256,
        },
        false,
        ['encrypt', 'decrypt'],
    )
}

async function encryptLocalWalletPayload(
    walletText: string,
    walletAccessKeyWif: string,
): Promise<{ encryptedWalletPayload: string; encryptedWalletNonce: string }> {
    const webCrypto = requireWebCrypto()
    const payloadKey = await deriveLocalWalletPayloadKey(walletAccessKeyWif)
    const nonce = webCrypto.getRandomValues(new Uint8Array(LOCAL_WALLET_NONCE_BYTES))
    const walletBytes = textEncoder.encode(walletText)
    const encryptedWalletPayload = await webCrypto.subtle.encrypt(
        {
            name: 'AES-GCM',
            iv: nonce,
        },
        payloadKey,
        walletBytes,
    )

    return {
        encryptedWalletPayload: bytesToBase64Url(new Uint8Array(encryptedWalletPayload)),
        encryptedWalletNonce: bytesToBase64Url(nonce),
    }
}

async function decryptLocalWalletPayload(
    payloadRecord: LocalEncryptedWalletPayloadRecord,
    walletAccessKeyWif: string,
): Promise<string> {
    const webCrypto = requireWebCrypto()
    const payloadKey = await deriveLocalWalletPayloadKey(walletAccessKeyWif)
    const nonce = base64UrlToBytes(payloadRecord.encryptedWalletNonce)
    const encryptedWalletBytes = base64UrlToBytes(payloadRecord.encryptedWalletPayload)
    const decryptedWalletBytes = await webCrypto.subtle.decrypt(
        {
            name: 'AES-GCM',
            iv: nonce,
        },
        payloadKey,
        encryptedWalletBytes,
    )

    return textDecoder.decode(decryptedWalletBytes)
}

function parseWalletTextForSelfCustody(walletText: string): {
    walletAccessKeyWif: string
    walletIdentity: WalletIdentityInfo
    shares: ShamirShares
} {
    const walletJson = JSON.parse(walletText)
    const walletIdentity = getWalletIdentityInfo(walletJson)

    if (!walletJson?.payPk || !walletIdentity) {
        throw new Error('Wallet JSON is missing payPk or ordPk')
    }

    const walletAccessKey = generateRandomKey()
    const shares = splitKey(walletAccessKey.wif)
    if (!verifyShares(shares.treechatShare, shares.clientShare, shares.address)) {
        throw new Error('Generated Shamir shares failed verification')
    }

    return {
        walletAccessKeyWif: walletAccessKey.wif,
        walletIdentity,
        shares,
    }
}

/**
 * Opens the IndexedDB database, creating it if necessary.
 */
function openDatabase(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION)

        request.onerror = () => {
            reject(new Error(`Failed to open IndexedDB: ${request.error?.message}`))
        }

        request.onsuccess = () => {
            resolve(request.result)
        }

        request.onupgradeneeded = event => {
            const db = (event.target as IDBOpenDBRequest).result

            // Create the client-shares store if it doesn't exist
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'userId' })
            }

            if (!db.objectStoreNames.contains(DEVICE_ID_STORE_NAME)) {
                db.createObjectStore(DEVICE_ID_STORE_NAME, { keyPath: 'userId' })
            }

            if (!db.objectStoreNames.contains(WALLET_PAYLOAD_STORE_NAME)) {
                db.createObjectStore(WALLET_PAYLOAD_STORE_NAME, { keyPath: 'userId' })
            }
        }
    })
}

/**
 * Generates a UUID v4 for device identification.
 */
function generateDeviceId(): string {
    // Use crypto.randomUUID if available (modern browsers)
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID()
    }

    // Fallback for older browsers
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0
        const v = c === 'x' ? r : (r & 0x3) | 0x8
        return v.toString(16)
    })
}

async function getDeviceIdRecord(userId: string): Promise<DeviceIdRecord | null> {
    const db = await openDatabase()

    return new Promise((resolve, reject) => {
        const transaction = db.transaction(DEVICE_ID_STORE_NAME, 'readonly')
        const store = transaction.objectStore(DEVICE_ID_STORE_NAME)
        const request = store.get(userId)

        request.onerror = () => {
            reject(new Error(`Failed to get device id: ${request.error?.message}`))
        }

        request.onsuccess = () => {
            resolve(request.result ?? null)
        }

        transaction.oncomplete = () => {
            db.close()
        }
    })
}

async function storeDeviceIdRecord(userId: string, deviceId: string): Promise<DeviceIdRecord> {
    const db = await openDatabase()
    const record: DeviceIdRecord = {
        userId,
        deviceId,
        createdAt: Date.now(),
    }

    return new Promise((resolve, reject) => {
        const transaction = db.transaction(DEVICE_ID_STORE_NAME, 'readwrite')
        const store = transaction.objectStore(DEVICE_ID_STORE_NAME)
        const request = store.put(record)

        request.onerror = () => {
            reject(new Error(`Failed to store device id: ${request.error?.message}`))
        }

        request.onsuccess = () => {
            resolve(record)
        }

        transaction.oncomplete = () => {
            db.close()
        }
    })
}

/**
 * Returns a durable local device id even before this browser has a client share.
 *
 * This allows the requester in the SAS transfer flow to identify itself before
 * the wallet has been transferred and split on this device.
 */
export async function getOrCreateLocalDeviceId(userId: string): Promise<string> {
    const existingShare = await getClientShare(userId)
    if (existingShare?.deviceId) {
        await storeDeviceIdRecord(userId, existingShare.deviceId)
        return existingShare.deviceId
    }

    const existingDeviceId = await getDeviceIdRecord(userId)
    if (existingDeviceId?.deviceId) {
        return existingDeviceId.deviceId
    }

    const deviceId = generateDeviceId()
    await storeDeviceIdRecord(userId, deviceId)
    return deviceId
}

/**
 * Stores a client share in IndexedDB for an explicit, pre-existing device id.
 *
 * The SAS transfer flow uses this after the requesting device decrypts the full
 * wallet and runs the usual split/store/register step for its durable device id.
 */
export async function storeClientShareForDevice(
    userId: string,
    deviceId: string,
    clientShare: string,
    address: string,
): Promise<ClientShareRecord> {
    const db = await openDatabase()
    const record: ClientShareRecord = {
        userId,
        deviceId,
        clientShare,
        address,
        createdAt: Date.now(),
    }

    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME, DEVICE_ID_STORE_NAME], 'readwrite')
        const clientShareStore = transaction.objectStore(STORE_NAME)
        const deviceIdStore = transaction.objectStore(DEVICE_ID_STORE_NAME)
        const clientShareRequest = clientShareStore.put(record)
        const deviceIdRequest = deviceIdStore.put({
            userId,
            deviceId,
            createdAt: record.createdAt,
        })

        let writeFailed = false

        const rejectOnce = (message: string) => {
            if (writeFailed) {
                return
            }

            writeFailed = true
            reject(new Error(message))
        }

        clientShareRequest.onerror = () => {
            rejectOnce(`Failed to store client share: ${clientShareRequest.error?.message}`)
        }

        deviceIdRequest.onerror = () => {
            rejectOnce(`Failed to persist device id: ${deviceIdRequest.error?.message}`)
        }

        transaction.oncomplete = () => {
            persistStoredClientSigningDeviceId(userId, deviceId)
            resolve(record)
            db.close()
        }

        transaction.onerror = () => {
            rejectOnce(`Failed to store client share: ${transaction.error?.message}`)
            db.close()
        }
    })
}

export async function storeLocalEncryptedWalletPayloadForDevice(
    userId: string,
    deviceId: string,
    walletText: string,
    walletAccessKeyWif: string,
): Promise<LocalEncryptedWalletPayloadRecord> {
    const encryptedWalletEnvelope = await encryptLocalWalletPayload(walletText, walletAccessKeyWif)
    const record: LocalEncryptedWalletPayloadRecord = {
        userId,
        deviceId,
        formatVersion: LOCAL_WALLET_PAYLOAD_FORMAT_VERSION,
        encryptedWalletPayload: encryptedWalletEnvelope.encryptedWalletPayload,
        encryptedWalletNonce: encryptedWalletEnvelope.encryptedWalletNonce,
        createdAt: Date.now(),
    }

    return putLocalEncryptedWalletPayloadRecord(record)
}

export async function putLocalEncryptedWalletPayloadRecord(
    record: LocalEncryptedWalletPayloadRecord,
): Promise<LocalEncryptedWalletPayloadRecord> {
    const db = await openDatabase()

    return new Promise((resolve, reject) => {
        const transaction = db.transaction(WALLET_PAYLOAD_STORE_NAME, 'readwrite')
        const store = transaction.objectStore(WALLET_PAYLOAD_STORE_NAME)
        const request = store.put(record)

        request.onerror = () => {
            reject(new Error(`Failed to store encrypted wallet payload: ${request.error?.message}`))
        }

        request.onsuccess = () => {
            resolve(record)
        }

        transaction.oncomplete = () => {
            db.close()
        }

        transaction.onerror = () => {
            reject(
                new Error(
                    `Failed to store encrypted wallet payload: ${transaction.error?.message}`,
                ),
            )
            db.close()
        }
    })
}

export async function getLocalEncryptedWalletPayload(
    userId: string,
): Promise<LocalEncryptedWalletPayloadRecord | null> {
    const db = await openDatabase()

    return new Promise((resolve, reject) => {
        const transaction = db.transaction(WALLET_PAYLOAD_STORE_NAME, 'readonly')
        const store = transaction.objectStore(WALLET_PAYLOAD_STORE_NAME)
        const request = store.get(userId)

        request.onerror = () => {
            reject(new Error(`Failed to get encrypted wallet payload: ${request.error?.message}`))
        }

        request.onsuccess = () => {
            resolve(request.result ?? null)
        }

        transaction.oncomplete = () => {
            db.close()
        }
    })
}

export async function getLocalSelfCustodyPayloadStatus(
    userId: string,
): Promise<LocalSelfCustodyPayloadStatus> {
    const payloadRecord = await getLocalEncryptedWalletPayload(userId)

    if (!payloadRecord) {
        return 'missing'
    }

    if (payloadRecord.formatVersion !== LOCAL_WALLET_PAYLOAD_FORMAT_VERSION) {
        return 'legacy'
    }

    return 'compatible'
}

export async function persistSelfCustodyWalletForDevice(
    userId: string,
    deviceId: string,
    walletText: string,
    // Shadow mode (lib/localSigningShadow) decides itself whether asking is silent.
    { requestPersistence = true }: { requestPersistence?: boolean } = {},
): Promise<PersistedSelfCustodyWallet> {
    const preparedWallet = parseWalletTextForSelfCustody(walletText)
    const clientShareRecord = await storeClientShareForDevice(
        userId,
        deviceId,
        preparedWallet.shares.clientShare,
        preparedWallet.walletIdentity.paymentAddress,
    )
    const walletPayloadRecord = await storeLocalEncryptedWalletPayloadForDevice(
        userId,
        deviceId,
        walletText,
        preparedWallet.walletAccessKeyWif,
    )

    // Persistence is eviction protection only. Keep it best-effort so a denied
    // or unsupported browser request never prevents wallet setup or restore.
    if (requestPersistence) void requestPersistentStorage()

    return {
        clientShareRecord,
        walletPayloadRecord,
        shares: preparedWallet.shares,
        walletIdentity: preparedWallet.walletIdentity,
    }
}

function recoveryStateSnapshot(records: unknown[]): string {
    return JSON.stringify(records.map(record => record ?? null))
}

async function readRecoveryState(userId: string): Promise<{
    snapshot: string
    previousDeviceId: string | null
}> {
    const db = await openDatabase()
    return new Promise((resolve, reject) => {
        const names = [STORE_NAME, DEVICE_ID_STORE_NAME, WALLET_PAYLOAD_STORE_NAME]
        const transaction = db.transaction(names, 'readonly')
        const requests = names.map(name => transaction.objectStore(name).get(userId))
        transaction.oncomplete = () => {
            db.close()
            resolve({
                snapshot: recoveryStateSnapshot(requests.map(request => request.result)),
                previousDeviceId:
                    requests[0]?.result?.deviceId ?? requests[1]?.result?.deviceId ?? null,
            })
        }
        transaction.onabort = () => {
            db.close()
            reject(new Error('Could not read the existing local wallet before recovery'))
        }
        transaction.onerror = () => {
            // IndexedDB aborts the transaction on an unhandled request error.
        }
    })
}

/** Stage a new device share pair without changing the active wallet. */
export async function prepareRecoveredWalletForDevice(
    userId: string,
    newDeviceId: string,
    walletText: string,
    expectedIdentity: WalletIdentityInfo,
): Promise<PreparedRecoveredWallet> {
    if (
        typeof userId !== 'string' ||
        !userId.length ||
        typeof newDeviceId !== 'string' ||
        !newDeviceId.length ||
        newDeviceId.length > 128
    ) {
        throw new Error('Recovery requires an account and a fresh device identifier')
    }
    validateWalletTextForRecovery(walletText, expectedIdentity)
    const previous = await readRecoveryState(userId)
    if (newDeviceId === previous.previousDeviceId) {
        throw new Error('Wallet recovery must register a fresh device identifier')
    }
    const preparedWallet = parseWalletTextForSelfCustody(walletText)
    const encrypted = await encryptLocalWalletPayload(walletText, preparedWallet.walletAccessKeyWif)
    const createdAt = Date.now()
    const prepared: PreparedRecoveredWallet = Object.freeze({
        userId,
        deviceId: newDeviceId,
        previousDeviceId: previous.previousDeviceId,
        walletIdentity: Object.freeze(preparedWallet.walletIdentity),
        shares: Object.freeze(preparedWallet.shares),
        clientShareRecord: Object.freeze({
            userId,
            deviceId: newDeviceId,
            clientShare: preparedWallet.shares.clientShare,
            address: preparedWallet.walletIdentity.paymentAddress,
            createdAt,
        }),
        walletPayloadRecord: Object.freeze({
            userId,
            deviceId: newDeviceId,
            formatVersion: LOCAL_WALLET_PAYLOAD_FORMAT_VERSION,
            ...encrypted,
            createdAt,
        }),
    })
    preparedRecoveryStates.set(prepared, previous.snapshot)
    return prepared
}

/** Call only after the server has accepted this fresh device's server share. */
export async function commitRecoveredWalletForDevice(
    prepared: PreparedRecoveredWallet,
): Promise<PersistedSelfCustodyWallet> {
    const expectedSnapshot = preparedRecoveryStates.get(prepared)
    if (expectedSnapshot === undefined)
        throw new Error('Wallet recovery was not prepared on this page')
    const db = await openDatabase()
    return new Promise((resolve, reject) => {
        const names = [STORE_NAME, DEVICE_ID_STORE_NAME, WALLET_PAYLOAD_STORE_NAME]
        const transaction = db.transaction(names, 'readwrite')
        const requests = names.map(name => transaction.objectStore(name).get(prepared.userId))
        let pending = requests.length
        let failure =
            'The recovered wallet could not be saved; the previous local wallet was preserved'
        for (const request of requests) {
            request.onsuccess = () => {
                pending -= 1
                if (pending !== 0) return
                if (recoveryStateSnapshot(requests.map(item => item.result)) !== expectedSnapshot) {
                    failure =
                        'The local wallet changed during recovery. Retry recovery in this tab.'
                    transaction.abort()
                    return
                }
                // Keep reads and writes in one transaction: another tab cannot interleave a change.
                try {
                    transaction.objectStore(STORE_NAME).put(prepared.clientShareRecord)
                    transaction.objectStore(DEVICE_ID_STORE_NAME).put({
                        userId: prepared.userId,
                        deviceId: prepared.deviceId,
                        createdAt: prepared.clientShareRecord.createdAt,
                    })
                    transaction
                        .objectStore(WALLET_PAYLOAD_STORE_NAME)
                        .put(prepared.walletPayloadRecord)
                } catch {
                    transaction.abort()
                }
            }
        }
        transaction.oncomplete = () => {
            db.close()
            preparedRecoveryStates.delete(prepared)
            persistStoredClientSigningDeviceId(prepared.userId, prepared.deviceId)
            void requestPersistentStorage()
            resolve(prepared)
        }
        transaction.onabort = () => {
            db.close()
            reject(new Error(failure))
        }
        transaction.onerror = () => {
            // Let the default request error action abort and roll back every store.
        }
    })
}

export async function recoverLocalWalletTextWithShares(
    userId: string,
    treechatShare: string,
    clientShare: string,
): Promise<string> {
    const payloadRecord = await getLocalEncryptedWalletPayload(userId)
    if (!payloadRecord) {
        throw new Error(missingSelfCustodyWalletPayloadMessage())
    }

    if (payloadRecord.formatVersion !== LOCAL_WALLET_PAYLOAD_FORMAT_VERSION) {
        throw new Error(legacySelfCustodyWalletPayloadMessage())
    }

    const walletAccessKeyWif = recombineShares(treechatShare, clientShare)
    if (!walletAccessKeyWif) {
        throw new Error('Failed to recombine this device share pair')
    }

    try {
        const walletText = await decryptLocalWalletPayload(payloadRecord, walletAccessKeyWif)
        parseWalletTextForSelfCustody(walletText)
        return walletText
    } catch (error) {
        if (isSelfCustodyReinitializationRequiredError(error)) {
            throw error
        }

        throw new Error(unreadableSelfCustodyWalletPayloadMessage())
    }
}

export async function recoverPaymentKeyWifWithShares(
    userId: string,
    treechatShare: string,
    clientShare: string,
    expectedAddress?: string,
): Promise<string> {
    const walletText = await recoverLocalWalletTextWithShares(userId, treechatShare, clientShare)
    const walletJson = JSON.parse(walletText)
    const paymentKeyWif = walletJson?.payPk

    if (!paymentKeyWif) {
        throw new Error('Recovered wallet JSON is missing payPk')
    }

    if (expectedAddress) {
        const paymentKeyInfo = getKeyInfo(paymentKeyWif)
        if (!paymentKeyInfo?.address) {
            throw new Error('Recovered wallet payment key is invalid')
        }

        if (paymentKeyInfo.address !== expectedAddress) {
            throw new Error('Recovered wallet payment key does not match the expected address')
        }
    }

    return paymentKeyWif
}

/**
 * Stores a client share in IndexedDB for a user.
 * If a record already exists for this user, it will be overwritten.
 * Generates a new deviceId if this is the first time storing for this user.
 *
 * @param userId - The user's ID
 * @param clientShare - The Shamir client share to store
 * @param address - The BSV address for verification
 * @returns The stored record (including the deviceId)
 */
export async function storeClientShare(
    userId: string,
    clientShare: string,
    address: string,
): Promise<ClientShareRecord> {
    const deviceId = await getOrCreateLocalDeviceId(userId)
    return storeClientShareForDevice(userId, deviceId, clientShare, address)
}

/**
 * Retrieves the client share record for a user from IndexedDB.
 *
 * @param userId - The user's ID
 * @returns The stored record, or null if not found
 */
export async function getClientShare(userId: string): Promise<ClientShareRecord | null> {
    const db = await openDatabase()

    return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readonly')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.get(userId)

        request.onerror = () => {
            reject(new Error(`Failed to get client share: ${request.error?.message}`))
        }

        request.onsuccess = () => {
            const record = request.result ?? null
            if (record?.deviceId) {
                persistStoredClientSigningDeviceId(userId, record.deviceId)
            }
            resolve(record)
        }

        transaction.oncomplete = () => {
            db.close()
        }
    })
}

/**
 * Removes the client share for a user from IndexedDB.
 * Use this on logout or when revoking device access.
 *
 * @param userId - The user's ID
 * @returns true if a record was deleted, false if no record existed
 */
export async function clearClientShare(userId: string): Promise<boolean> {
    const db = await openDatabase()

    // Check if record exists first
    const existing = await getClientShare(userId)
    if (!existing) {
        db.close()
        return false
    }

    return new Promise((resolve, reject) => {
        const transaction = db.transaction([STORE_NAME, WALLET_PAYLOAD_STORE_NAME], 'readwrite')
        const clientShareStore = transaction.objectStore(STORE_NAME)
        const walletPayloadStore = transaction.objectStore(WALLET_PAYLOAD_STORE_NAME)
        const clientShareRequest = clientShareStore.delete(userId)
        const walletPayloadRequest = walletPayloadStore.delete(userId)

        clientShareRequest.onerror = () => {
            reject(new Error(`Failed to clear client share: ${clientShareRequest.error?.message}`))
        }

        walletPayloadRequest.onerror = () => {
            reject(
                new Error(
                    `Failed to clear encrypted wallet payload: ${walletPayloadRequest.error?.message}`,
                ),
            )
        }

        transaction.oncomplete = () => {
            clearStoredClientSigningDeviceId(userId)
            resolve(true)
            db.close()
        }

        transaction.onerror = () => {
            reject(new Error(`Failed to clear client share: ${transaction.error?.message}`))
            db.close()
        }
    })
}

/**
 * Lists all stored client share records.
 * Useful for debugging and admin purposes.
 *
 * @returns Array of all stored records
 */
export async function listAllClientShares(): Promise<ClientShareRecord[]> {
    const db = await openDatabase()

    return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, 'readonly')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.getAll()

        request.onerror = () => {
            reject(new Error(`Failed to list client shares: ${request.error?.message}`))
        }

        request.onsuccess = () => {
            resolve(request.result)
        }

        transaction.oncomplete = () => {
            db.close()
        }
    })
}
